import { Component, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { Routes, RouterLink } from '@angular/router'
import { FormsModule } from '@angular/forms'
import { TranslateService } from '@ngx-translate/core'
import { LocalStorage } from 'ngx-store'

import { ClientSession, ElementsUserMiniWithAvatar, GetAllWorkstationsSortEnum, Workstation } from 'elements-sdk'
import { APIService, extractPaginatedList, PaginatedList } from 'api/services/api.service'
import { RXJS_SCHEDULER } from 'common/helpers'
import { ListResourceController, ListResourceFilterItemDirective, PaginationFilters, SearchQueryFilter, SortDescriptor } from 'common/listResourceController'
import { SocketService } from 'common/util/services/socket.service'
import { PageTitleComponent } from 'common/util/components/pageTitle.component'
import { TranslateDirective } from 'common/locale/directives/translate.directive'
import { ListResourceComponent } from 'common/listResourceController/listResource.component'
import { ListResourceFilterDirective } from 'common/listResourceController/listResourceFilter.directive'
import { CheckboxComponent } from 'common/util/components/checkbox.component'
import { ExtractDirective } from 'common/listResourceController/extract.directive'
import { WithPaginationComponent } from 'common/listResourceController/withPagination.component'
import { AvatarComponent } from 'common/util/components/avatar.component'
import { SpinnerComponent } from 'common/util/components/spinner.component'
import { TranslatePipe } from 'common/locale'
import { permissionGuard } from 'main/services/identity.service'

interface WorkstationInterface {
  family: string
  address: string
  mac: string
  internal: boolean
  netmask: string
  scopeid: number
  cidr: string
}

interface WorkstationItem {
  readonly id: string
  readonly displayName: string
  readonly lastSeen: Date | null
  readonly ipAddresses: string // A comma-separated list, ready for rendering. Searching might be too literal but good enough (e.g. will match commas).
  readonly workspaces: string // Same as above.
  readonly connectedUser: ElementsUserMiniWithAvatar | null
  readonly connectedUsersCount: number
}

export function analyzeData (
  workstations: ReadonlyArray<Workstation>,
  clientSessions: ReadonlyArray<ClientSession>,
) {
  const workstationItems = new Array<WorkstationItem>()
  const connectedUsersCount = clientSessions.length
  const seenWorkstationIds = new Set(clientSessions.map(session => session.workstation.id))
  const connectedWorkstationsCount = seenWorkstationIds.size
  const mountedWorkspaces = clientSessions.flatMap(s => s.mountedWorkspaces)
  const smbMountedWorkspacesCount = mountedWorkspaces.filter(mw => mw.protocol === 'smb').length
  const afpMountedWorkspacesCount = mountedWorkspaces.filter(mw => mw.protocol === 'afp').length

  for (const workstation of workstations) {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    let ipAddressList = Object.values(workstation.report?.interfaces as Record<string, Array<WorkstationInterface>> | null ?? {})
      .flat()
      .filter((interface_) => interface_.family === 'IPv4' && interface_.address !== '127.0.0.1')
      .map((interface_) => interface_.address)
    if (workstation.address && ipAddressList.includes(workstation.address)) {
      // Order the "main" in front so that sorting makes sense visually
      ipAddressList = [workstation.address, ...ipAddressList.filter(x => x !== workstation.address)]
    }

    const ipAddresses = ipAddressList.join(', ')

    const workstationSessions = clientSessions.filter((clientSession) => clientSession.workstation.id === workstation.id)

    const workspaces = workstationSessions
      .flatMap((session) => session.mountedWorkspaces)
      .map((mountedWorkspace) => mountedWorkspace.workspace.name)
      .join(', ')

    workstationItems.push({
      id: workstation.id,
      displayName: workstation.displayName,
      lastSeen: workstation.lastSeen,
      ipAddresses,
      workspaces,
      connectedUser: workstationSessions[0]?.user ?? null,
      connectedUsersCount: workstationSessions.length,
    })
  }

  return {
    workstationItems,
    connectedWorkstationsCount,
    connectedUsersCount,
    smbMountedWorkspacesCount,
    afpMountedWorkspacesCount,
  }
}

@Component({
  selector: 'workstations-view',
  templateUrl: './workstationsView.component.html',
  imports: [
    PageTitleComponent,
    TranslateDirective,
    ListResourceComponent,
    ListResourceFilterDirective,
    ListResourceFilterItemDirective,
    CheckboxComponent,
    FormsModule,
    ExtractDirective,
    WithPaginationComponent,
    RouterLink,
    AvatarComponent,
    SpinnerComponent,
    TranslatePipe,
  ],
})
export class WorkstationsViewComponent {
  readonly sortDescriptors = [
    SortDescriptor<GetAllWorkstationsSortEnum>(GetAllWorkstationsSortEnum.Name, {
      displayName: this.translate.instant(`By name`),
      icon: 'arrow-down-a-z',
    }),
    SortDescriptor(GetAllWorkstationsSortEnum.NameReverse, {
      displayName: this.translate.instant(`By name (reverse)`),
      icon: 'arrow-up-z-a',
    }),
    SortDescriptor(GetAllWorkstationsSortEnum.ClientUsername, {
      displayName: this.translate.instant(`By username`),
      icon: 'arrow-down-a-z',
    }),
    SortDescriptor(GetAllWorkstationsSortEnum.ClientUsernameReverse, {
      displayName: this.translate.instant(`By username (reverse)`),
      icon: 'arrow-up-z-a',
    }),
    SortDescriptor(GetAllWorkstationsSortEnum.Address, {
      displayName: this.translate.instant(`By IP`),
      icon: 'arrow-down-1-9',
    }),
    SortDescriptor(GetAllWorkstationsSortEnum.AddressReverse, {
      displayName: this.translate.instant(`By IP (reverse)`),
      icon: 'arrow-up-9-1',
    }),
    SortDescriptor(GetAllWorkstationsSortEnum.LastSeenReverse, {
      displayName: this.translate.instant(`Recently seen`),
      icon: 'arrow-down-short-wide',
    }),
    SortDescriptor(GetAllWorkstationsSortEnum.LastSeen, {
      displayName: this.translate.instant(`Not seen recently`),
      icon: 'arrow-up-wide-short',
    }),
  ]

  readonly controller = ListResourceController({
    pagination: PaginationFilters(),
    searchQuery: SearchQueryFilter('workstations'),
    sortDescriptors: this.sortDescriptors,
    apply: async ({ pagination: { pageSize, page }, searchQuery, sortKey }, signal) => {
      const [clientSessions, allWorkstationsRawApiResponse] = await Promise.all([
        this.api.main.getAllClientSessions(undefined, { signal }),
        this.api.main.getAllWorkstationsRaw({
          limit: pageSize,
          offset: (page - 1) * pageSize,
          filter: searchQuery.string,
          filteringMode: searchQuery.mode,
          sort: sortKey,
        }, { signal }),
      ])
      const workstationsPaginatedList = await extractPaginatedList(allWorkstationsRawApiResponse)
      const { workstationItems, ...extra } = analyzeData(workstationsPaginatedList.items, clientSessions)
      return {
        // Data analysis doesn't change the number of workstation items; it only transforms them.
        list: PaginatedList(workstationItems, workstationsPaginatedList.first, workstationsPaginatedList.total),
        extra,
      }
    },
    defaultRefreshMetadata: { stealthy: true },
    scheduler: inject(RXJS_SCHEDULER),
  })

  @LocalStorage('showWorkstationIps') showWorkstationIps = false
  @LocalStorage('showMountedWorkspaces') showMountedWorkspaces = false

  toggleShowWorkstationIps () {
    this.showWorkstationIps = !this.showWorkstationIps
  }

  toggleShowMountedWorkspaces () {
    this.showMountedWorkspaces = !this.showMountedWorkspaces
  }

  constructor (
    private readonly api: APIService,
    private readonly socket: SocketService,
    private readonly translate: TranslateService,
  ) {
    this.socket.subject('mounted-workspaces:updated')
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.controller.refresh({ stealthy: true })
      })
  }

  static readonly ROUTES: Routes = [
    {
      path: '',
      component: WorkstationsViewComponent,
      canActivate: [permissionGuard('workstations:view')],
    },
  ]
}
